import React from "react";
import { Link, useLocalSearchParams, usePathname } from "expo-router";
import {
  SafeAreaView,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";

import SeoHead from "@/components/seo/SeoHead";
import {
  getGeneratedPublicSearchLanding,
  getGeneratedPublicSearchLandingSlugs,
  type PublicSearchLandingBusiness,
} from "@/lib/publicSearchLandingSeo";
import {
  SEO_DEFAULT_IMAGE_URL,
  buildCanonicalUrl,
  buildSearchLandingSeo,
} from "@/lib/seo";

export function generateStaticParams() {
  return getGeneratedPublicSearchLandingSlugs().map((slug) => ({ slug }));
}

function readSlugFromPath(pathname: string): string {
  const prefix = "/s/";

  if (!pathname.startsWith(prefix)) return "";

  const encodedSlug = pathname.slice(prefix.length).split("/")[0] || "";

  try {
    return decodeURIComponent(encodedSlug);
  } catch {
    return encodedSlug;
  }
}

function labelLine(values: (string | null)[]): string {
  return values.filter(Boolean).join(" | ");
}

function locationLine(business: PublicSearchLandingBusiness): string {
  return [business.areaName, business.cityName, business.countyName]
    .filter(Boolean)
    .join(", ");
}

function LandingResult({ business }: { business: PublicSearchLandingBusiness }) {
  const profileHref = {
    pathname: "/p/[slug]",
    params: { slug: business.slug },
  } as const;
  const category = labelLine([
    business.categoryName,
    business.subcategoryName,
  ]);
  const location = locationLine(business);
  const itemNames = business.itemNames.slice(0, 4).join(", ");

  return (
    <View style={styles.resultCard}>
      <Link href={profileHref} style={styles.resultTitle}>
        {business.name}
      </Link>

      {!!category && <Text style={styles.resultMeta}>{category}</Text>}
      {!!location && <Text style={styles.resultMeta}>{location}</Text>}

      {!!business.description && (
        <Text style={styles.resultDescription} numberOfLines={3}>
          {business.description}
        </Text>
      )}

      {!!itemNames && <Text style={styles.resultMeta}>Services: {itemNames}</Text>}

      <Link href={profileHref} style={styles.profileLink}>
        View business profile
      </Link>
    </View>
  );
}

export default function PublicSearchLandingPage() {
  const { slug } = useLocalSearchParams<{ slug?: string }>();
  const pathname = usePathname();
  const routeSlug = typeof slug === "string" ? slug : "";
  const pathSlug = React.useMemo(() => readSlugFromPath(pathname), [pathname]);
  const landingSlug = routeSlug || pathSlug;
  const landing = React.useMemo(
    () => getGeneratedPublicSearchLanding(landingSlug),
    [landingSlug]
  );
  const seo = React.useMemo(
    () => (landing ? buildSearchLandingSeo(landing) : null),
    [landing]
  );

  if (!landing || !seo) {
    return (
      <>
        <SeoHead
          title="Search Page Not Found | Soko Grid"
          description="This Soko Grid search landing page is not available."
          canonicalUrl={buildCanonicalUrl(
            `/s/${encodeURIComponent(landingSlug)}`
          )}
          imageUrl={SEO_DEFAULT_IMAGE_URL}
          noIndex
        />

        <SafeAreaView style={styles.safeArea}>
          <View style={styles.missingWrap}>
            <Text style={styles.missingTitle}>Search page unavailable</Text>
            <Text style={styles.missingText}>
              This public Soko Grid search page is not available.
            </Text>
          </View>
        </SafeAreaView>
      </>
    );
  }

  const heading = `${landing.queryLabel} in ${landing.locationLabel}`;
  const resultCount = landing.businesses.length;
  const introText = `Find ${landing.queryLabel.toLowerCase()} in ${landing.locationLabel} on Soko Grid. Browse trusted public business profiles with services, locations, descriptions, and profile links.`;
  const resultsHeading =
    resultCount === 1 ? "Available business" : "Available businesses";
  const resultsSubtext = `Explore Soko Grid business profiles offering ${landing.queryLabel.toLowerCase()} in ${landing.locationLabel}.`;

  return (
    <>
      <SeoHead {...seo} />

      <SafeAreaView style={styles.safeArea}>
        <ScrollView
          style={styles.page}
          contentContainerStyle={styles.content}
        >
          <View style={styles.hero}>
            <Text accessibilityRole="header" style={styles.title}>
              {heading}
            </Text>
            <Text style={styles.intro}>{introText}</Text>
          </View>

          <View style={styles.resultsHeader}>
            <Text style={styles.resultsTitle}>{resultsHeading}</Text>
            <Text style={styles.resultsSubtitle}>{resultsSubtext}</Text>
          </View>

          <View style={styles.resultsList}>
            {landing.businesses.map((business) => (
              <LandingResult key={business.slug} business={business} />
            ))}
          </View>
        </ScrollView>
      </SafeAreaView>
    </>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: "#f8fafc",
  },
  page: {
    flex: 1,
  },
  content: {
    width: "100%",
    maxWidth: 980,
    alignSelf: "center",
    paddingHorizontal: 18,
    paddingVertical: 32,
  },
  hero: {
    marginBottom: 24,
  },
  title: {
    fontSize: 34,
    lineHeight: 40,
    fontWeight: "800",
    color: "#111827",
  },
  intro: {
    marginTop: 12,
    maxWidth: 720,
    fontSize: 16,
    lineHeight: 24,
    color: "#475569",
  },
  resultsHeader: {
    marginBottom: 14,
  },
  resultsTitle: {
    fontSize: 18,
    fontWeight: "800",
    color: "#111827",
  },
  resultsSubtitle: {
    marginTop: 4,
    fontSize: 14,
    color: "#64748b",
  },
  resultsList: {
    gap: 12,
  },
  resultCard: {
    borderWidth: 1,
    borderColor: "#e2e8f0",
    borderRadius: 8,
    backgroundColor: "#fff",
    padding: 16,
  },
  resultTitle: {
    fontSize: 18,
    lineHeight: 24,
    fontWeight: "800",
    color: "#0f172a",
    textDecorationLine: "none",
  },
  resultMeta: {
    marginTop: 6,
    fontSize: 14,
    lineHeight: 20,
    color: "#475569",
  },
  resultDescription: {
    marginTop: 8,
    fontSize: 14,
    lineHeight: 20,
    color: "#334155",
  },
  profileLink: {
    marginTop: 12,
    fontSize: 14,
    lineHeight: 20,
    fontWeight: "700",
    color: "#0f766e",
    textDecorationLine: "none",
  },
  missingWrap: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
  },
  missingTitle: {
    fontSize: 22,
    fontWeight: "800",
    color: "#111827",
    textAlign: "center",
  },
  missingText: {
    marginTop: 8,
    fontSize: 14,
    lineHeight: 20,
    color: "#64748b",
    textAlign: "center",
  },
});
